//dependencies
import { useQueryClient } from "@tanstack/react-query";
import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";

//components
import { Avatar, ButtonStandard } from "@performyard/py-components";

import ExpandableAvatarGroup from "../../../components/AvatarGroup/ExpandableAvatarGroup";
import RotatingIconButton from "../../../components/Button/templates/RotatingIconButton";
import Card from "../../../components/Card";
import Container from "../../../components/Container";
import EmployeeCard from "../../../components/EmployeeCard";
import { Grid, GridCol } from "../../../components/Grid";
import Icon from "../../../components/Icon/Icon";
import Link from "../../../components/Link";
import Spacer from "../../../components/Spacer";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import Typography from "../../../components/Text";
import AvatarModal from "../EmployeeDetails/components/AvatarModal";
import DeactivateUserModal from "../EmployeeDetails/components/DeactivateUserModal";
import EditEmployeeCard from "../EmployeeDetails/components/EditEmployeeCard";
import { OptionsDropdown } from "../EmployeeDetails/components/OptionsDropdown";
import ReactivateUserModal from "../EmployeeDetails/components/ReactivateUserModal";
import ReassignManagerModal from "../EmployeeDetails/components/ReassignManagerModal";
import ViewEmployeeCard from "../EmployeeDetails/components/ViewEmployeeCard";

//hooks
import {
	avatarBlobUrlQueryKey,
	useCurrentUser,
	useEmployeeCustomFields,
	useInternalApiUser,
	useRbacEmployeePermissions,
	useSendInvite,
} from "../../../api/hooks";
import { useIsEngagementOnlyCompany } from "../../../hooks/useFeatureFlag";
import usePageWidthBreakpoint from "../../../hooks/usePageWidthBreakpoint";
import useSearchQueryParams from "../../../hooks/useSearchQueryParams";

//utils
import { employeeDetailsLinkWithFrom } from "../../../utils/employeeDetailsBreadcrumbs";
import { getValueForInputType } from "../utils";

//config
import Config from "../Config";
//styles
import css from "../EmployeeDetails/EmployeeDetails.module.scss";
import viewCss from "./EmployeeDetailsView.module.scss";

type RoleFieldValue = { _id: string; name: string };

const parseRoleFieldValue = (value: unknown): RoleFieldValue | null => {
	if (!value || typeof value !== "object") return null;
	const candidate = value as Record<string, unknown>;
	if (typeof candidate._id !== "string" || typeof candidate.name !== "string") return null;
	return { _id: candidate._id, name: candidate.name };
};

export const EmployeeDetailsView: React.FC = () => {
	const { userID = "" } = useParams<{ userID: string }>();
	const { queryParams, handleQueryChange } = useSearchQueryParams({ schema: Config.viewPageSchema });
	const navigate = useNavigate();

	const { isMobile, isTablet } = usePageWidthBreakpoint();

	const directReportsRef = useRef<HTMLButtonElement>(null);
	const contributesTosRef = useRef<HTMLButtonElement>(null);
	const queryClient = useQueryClient();

	const { data: currentUser, isLoading: isCurrentUserLoading, refetch: refetchCurrentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload?.user_id || "";
	const userId = userID || currentUserId || "";
	const avatarBlobUrl = queryClient.getQueryData<string>(avatarBlobUrlQueryKey(userId));

	const {
		data: userInfo,
		isLoading: isUserLoading,
		refetch: refetchInternalUser,
	} = useInternalApiUser(userId, {
		enabled: !!userId,
	});

	const {
		data = { customFields: [] },
		isLoading,
		refetch: refetchCustomFields,
	} = useEmployeeCustomFields(userId, {
		enabled: !!userId,
	});

	const { data: rbac, isLoading: isRbacLoading } = useRbacEmployeePermissions(userId, "employee_details", !userId);
	const { data: dashboardRbac, isLoading: isDashboardRbacLoading } = useRbacEmployeePermissions(
		userId,
		"dashboard",
		!userId
	);
	const { data: goalRbac } = useRbacEmployeePermissions(userId, "goal", !userId);
	const { data: reviewRbac } = useRbacEmployeePermissions(userId, "review", !userId);
	const { mutate: invite, isPending: isInviting } = useSendInvite({
		onSuccess: () => {
			refetchInternalUser();
			refetchCustomFields();
			refetchCurrentUser();
		},
	});

	const [directReportsExpanded, setDirectReportsExpanded] = useState<boolean>(false);
	const [contributorsExpanded, setContributorsExpanded] = useState<boolean>(false);

	const canEmployeeChangeAvatar = rbac?.includes("employee_details:changeAvatar");
	const { value: isEngagementOnlyCompany, query: engagementOnlyQuery } = useIsEngagementOnlyCompany();
	// /user/:id is a SPA route, so the server's deactivated and engagement-only redirects
	// can't be relied on here
	const canViewEmployeeDashboard =
		dashboardRbac?.includes("dashboard:viewEmployeeDashboard") &&
		userId !== currentUserId &&
		!userInfo?.deactivated &&
		!isEngagementOnlyCompany;
	const isDashboardGateLoading = isUserLoading || engagementOnlyQuery.isLoading;

	const customFields = data.customFields;

	const chosenMainCard = queryParams.editing ? (
		<EditEmployeeCard employeeId={userId} />
	) : (
		<ViewEmployeeCard employeeId={userId} />
	);

	const mainCard = isLoading || isCurrentUserLoading || isRbacLoading ? <ComponentLoader /> : chosenMainCard;

	const jobTitle = customFields.find((field) => field.name === "Job Title")?.value;
	const role = parseRoleFieldValue(customFields.find((field) => field.name === "Role")?.value);
	const managerField = customFields.find((field) => field.name === "Manager");

	const secondaryColumn = [
		<EmployeeCard
			isLoading={isUserLoading}
			employeeData={{
				id: userInfo?._id.toString() || "",
				name: userInfo?.name,
				isActive: !userInfo?.deactivated,
				jobTitle: String(jobTitle),
				role: role?.name,
				email: userInfo?.email || "",
			}}
			className={css.employeeCard}
			key="employeeCard"
			avatarButtonLabel={
				canEmployeeChangeAvatar && (
					<Container gap="4px">
						<Typography size="paragraph-3" weight="regular" color="neutral-0">
							Edit
						</Typography>
						<Icon size="small" icon={faPencil} color="neutral-0" />
					</Container>
				)
			}
			onAvatarButtonClick={canEmployeeChangeAvatar ? () => handleQueryChange({ modal: "avatar" }) : undefined}
			avatarUrl={avatarBlobUrl}
		>
			{canViewEmployeeDashboard && (
				<ButtonStandard
					color="primary"
					variant="outline"
					dangerouslySetClassName="w-fit mt-1"
					onClick={() => navigate(`/user/${userId}`)}
				>
					View Dashboard
				</ButtonStandard>
			)}
		</EmployeeCard>,
		<OptionsDropdown
			key="options-dropdown"
			employeeId={userId}
			currentUserId={currentUserId}
			userInfo={userInfo}
			employeeDetailsRbac={rbac}
			goalRbac={goalRbac}
			reviewRbac={reviewRbac}
			isInviting={isInviting}
			isAddFormModalOpen={queryParams.modal === "add360Form"}
			isCreateReviewModalOpen={queryParams.modal === "createReview"}
			onSendInvite={() => invite(userId)}
			onOpenModal={(modal) => handleQueryChange({ modal })}
			onCloseModal={() => handleQueryChange({ modal: undefined })}
		/>,
	];

	const isGroupVisible = customFields.some((field) => field.type === "department" && field.name === "Group");

	if (userInfo?.department && isGroupVisible) {
		secondaryColumn.push(
			<Card padding="8px 16px" key="department">
				<Typography size="paragraph-3" weight="semi-bold">
					Group
				</Typography>
				<Spacer height={1} />
				<Link href={`/group/employees/${userInfo.department._id}/`}>
					<Typography size="paragraph-3" weight="semi-bold">
						{userInfo.department.name}
					</Typography>
				</Link>
			</Card>
		);
	}

	if (userInfo?.departmentAdmin && userInfo.departmentAdmin.length > 0 && isGroupVisible) {
		secondaryColumn.push(
			<Card padding="8px 16px" key="admin-department">
				<Typography size="paragraph-3" weight="semi-bold">
					Groups Managed
				</Typography>
				<Spacer height={1} />
				{userInfo.departmentAdmin.map((group) => (
					<Link href={`/group/employees/${group._id.toString()}/`} key={group._id.toString()}>
						<Typography size="paragraph-3" weight="semi-bold">
							{group.name}
						</Typography>
					</Link>
				))}
			</Card>
		);
	}

	if (managerField && managerField.value) {
		const manager = getValueForInputType(managerField.type, managerField.value);
		secondaryColumn.push(
			<Card padding="8px 16px" key="manager">
				<Typography size="paragraph-3" weight="semi-bold">
					Manager
				</Typography>
				<Spacer height={1} />
				<Link href={employeeDetailsLinkWithFrom(manager?.value, currentUserId)}>
					<Container gap="8px" verticalAlignment="center">
						<Avatar size={24} id={manager?.value} name={manager?.label || "User"} />
						<Typography size="paragraph-3" weight="semi-bold">
							{manager?.label}
						</Typography>
					</Container>
				</Link>
			</Card>
		);
	}

	if (userInfo?.directReports && userInfo.directReports.length > 0) {
		const employees = userInfo.directReports.map((employee) => ({
			name: employee.name,
			_id: employee._id.toString(),
		}));
		secondaryColumn.push(
			<Card padding="8px 16px" key="directReports" onClick={() => directReportsRef?.current?.click()}>
				<Container gap="8px" verticalAlignment="center" horizontalAlignment="space-between">
					<Typography size="paragraph-3" weight="semi-bold">
						Direct Reports
					</Typography>
					{userInfo.directReports.length > 5 && (
						<RotatingIconButton
							icon={faAngleDown}
							onClick={() => setDirectReportsExpanded(!directReportsExpanded)}
							ref={directReportsRef}
							iconOnly
							isHollow={false}
							aria-label="Toggle direct reports"
						/>
					)}
				</Container>
				<Spacer height={1} />
				<ExpandableAvatarGroup
					maxLength={5}
					hasLinks
					employees={employees}
					size={24}
					expanded={directReportsExpanded || userInfo.directReports.length <= 5}
				/>
			</Card>
		);
	}

	if (userInfo?.contributesTo && userInfo.contributesTo.length > 0) {
		const employees = userInfo.contributesTo.map((employee) => ({
			name: employee.name,
			_id: employee._id.toString(),
		}));
		secondaryColumn.push(
			<Card padding="8px 16px" key="contributesTo" onClick={() => contributesTosRef?.current?.click()}>
				<Container gap="8px" verticalAlignment="center" horizontalAlignment="space-between">
					<Typography size="paragraph-3" weight="semi-bold">
						Contributes To
					</Typography>
					{userInfo.contributesTo.length > 5 && (
						<RotatingIconButton
							icon={faAngleDown}
							onClick={() => setContributorsExpanded(!contributorsExpanded)}
							style={{ border: "none", background: "none" }}
							ref={contributesTosRef}
							iconOnly
							isHollow={false}
							aria-label="Toggle contributes to"
						/>
					)}
				</Container>
				<Spacer height={1} />
				<ExpandableAvatarGroup
					maxLength={5}
					hasLinks
					employees={employees}
					size={24}
					expanded={contributorsExpanded || userInfo.contributesTo.length <= 5}
				/>
			</Card>
		);
	}

	const resolvedSecondaryColumn =
		isLoading || isCurrentUserLoading || isRbacLoading || isDashboardRbacLoading || isDashboardGateLoading
			? []
			: secondaryColumn;
	const isStackedView = isMobile || isTablet || resolvedSecondaryColumn.length === 0;

	return (
		<div>
			<DeactivateUserModal
				isOpen={queryParams.modal === "deactivate"}
				handleClose={() => handleQueryChange({ modal: undefined })}
				userID={userId}
			/>
			<ReactivateUserModal
				isOpen={queryParams.modal === "reactivate"}
				handleClose={() => handleQueryChange({ modal: undefined })}
				userID={userId}
			/>
			<AvatarModal
				isOpen={queryParams.modal === "avatar"}
				handleClose={() => handleQueryChange({ modal: undefined })}
				userID={userId}
			/>
			<ReassignManagerModal
				isOpen={queryParams.modal === "reassign"}
				handleClose={() => handleQueryChange({ modal: undefined, reassignIds: undefined })}
				userID={userId}
			/>
			<div className={viewCss.contentContainer}>
				<Grid
					cols={isStackedView ? 6 : 8}
					gap={2}
					className={`${viewCss.columnsContainer} ${isStackedView ? viewCss.stackedView : ""}`}
				>
					{resolvedSecondaryColumn.length > 0 && (
						<GridCol span={isStackedView ? 6 : 2}>
							<div className={viewCss.secondaryColumnContainer}>
								{resolvedSecondaryColumn.map((element, index) => (
									<div key={index}>{element}</div>
								))}
							</div>
						</GridCol>
					)}
					<GridCol span={6}>
						<div className={viewCss.mainColumnContainer}>
							<div>{mainCard}</div>
						</div>
					</GridCol>
				</Grid>
			</div>
		</div>
	);
};

export default EmployeeDetailsView;
